import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, Phone, MapPin, Send, Shield, QrCode } from "lucide-react";
import axios from "axios";
import ShareQrModal from "./ShareQrModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [subEmail, setSubEmail] = useState("");
  const [subStatus, setSubStatus] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail) return;
    try {
      const res = await axios.post(`${API}/subscribe`, { email: subEmail, source: "footer" });
      setSubStatus(res.data.status === "existing" ? "You're already subscribed." : "Welcome to the inner circle.");
      setSubEmail("");
    } catch { setSubStatus("Please try again."); }
  };

  return (
    <footer data-testid="main-footer" className="relative z-10 bg-luxury-black border-t border-white/5">
      {/* Newsletter Bar */}
      <div className="border-b border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl text-white mb-1">Stay in the Inner Circle</h3>
            <p className="text-white/40 font-body text-sm">Exclusive event invitations, curated recommendations, and VIP access.</p>
          </div>
          <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
            <input
              data-testid="footer-subscribe-email"
              type="email"
              value={subEmail}
              onChange={(e) => setSubEmail(e.target.value)}
              placeholder="your@email.com"
              className="bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-4 py-3 text-sm font-body w-full md:w-64 focus:outline-none focus:border-gold/50"
            />
            <button type="submit" data-testid="footer-subscribe-btn" className="bg-gold text-black px-5 py-3 hover:bg-gold-light transition-colors flex-shrink-0">
              <Send size={16} />
            </button>
          </form>
          {subStatus && <p className="text-gold text-xs font-body">{subStatus}</p>}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <h3 className="font-heading text-3xl font-light text-white mb-4">
              Aristocrats<span className="text-gold">JA</span>
            </h3>
            <p className="text-white/50 font-body text-sm leading-relaxed mb-6">
              Luxury event photography, cinematic videography, and elite production.
              Founded by Matthew Taylor. Reserved for the exceptional.
            </p>
            <div className="flex gap-4 mb-6">
              <a href="https://www.instagram.com/aristocratsja" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="text-white/40 hover:text-gold transition-colors"><Instagram size={20} /></a>
              <a href="https://www.linkedin.com/in/matthew-taylor-1b9265132/" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin" className="text-white/40 hover:text-gold transition-colors"><Linkedin size={20} /></a>
              <button onClick={() => setQrOpen(true)} data-testid="footer-share-qr" title="Share QR code"
                className="text-white/40 hover:text-gold transition-colors"><QrCode size={20} /></button>
            </div>
            <div className="flex items-start gap-2 p-3 bg-white/[0.02] border border-white/5">
              <Shield size={14} className="text-gold flex-shrink-0 mt-0.5" />
              <p className="text-white/30 text-[10px] font-body leading-relaxed">Your data is never sold to third parties. We respect your privacy and only use information to improve your experience.</p>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-body text-xs font-medium uppercase tracking-[0.2em] text-gold mb-6">Explore</h4>
            <div className="flex flex-col gap-3">
              {[
                { to: "/about", label: "About Us" },
                { to: "/services", label: "Services" },
                { to: "/portfolio", label: "Portfolio" },
                { to: "/recommendations", label: "Top 50 Lists" },
                { to: "/testimonials", label: "Testimonials" },
                { to: "/booking", label: "Book Now" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-white/50 hover:text-white text-sm font-light transition-colors">{link.label}</Link>
              ))}
            </div>
          </div>

          {/* Portals */}
          <div>
            <h4 className="font-body text-xs font-medium uppercase tracking-[0.2em] text-gold mb-6">Portals</h4>
            <div className="flex flex-col gap-3">
              {[
                { to: "/vip", label: "VIP Client Portal" },
                { to: "/partner", label: "Partner Portal" },
                { to: "/promotions", label: "Promotions" },
                { to: "/affiliates", label: "Affiliates" },
                { to: "/admin", label: "Admin" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-white/50 hover:text-white text-sm font-light transition-colors">{link.label}</Link>
              ))}
              <div className="mt-2">
                <h4 className="font-body text-xs font-medium uppercase tracking-[0.2em] text-gold mb-3">Legal</h4>
                <div className="flex flex-col gap-2">
                  <Link to="/privacy" className="text-white/50 hover:text-white text-sm font-light transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="text-white/50 hover:text-white text-sm font-light transition-colors">Terms & Conditions</Link>
                  <Link to="/support" data-testid="footer-support-link" className="text-white/50 hover:text-white text-sm font-light transition-colors">24-hour Support</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-body text-xs font-medium uppercase tracking-[0.2em] text-gold mb-6">Contact</h4>
            <div className="flex flex-col gap-4">
              <a href="mailto:aristocratsja@gmail.com" className="flex items-center gap-3 text-white/50 hover:text-white text-sm transition-colors"><Mail size={16} /> aristocratsja@gmail.com</a>
              <a href="tel:+18764390976" className="flex items-center gap-3 text-white/50 hover:text-white text-sm transition-colors"><Phone size={16} /> +1 (876) 439-0976</a>
              <a href="https://wa.me/18764390976" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/50 hover:text-white text-sm transition-colors">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.625-1.472A11.932 11.932 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-2.168 0-4.19-.572-5.942-1.573l-.426-.253-2.744.874.873-2.688-.278-.44A9.782 9.782 0 012.182 12c0-5.423 4.395-9.818 9.818-9.818S21.818 6.577 21.818 12s-4.395 9.818-9.818 9.818z"/></svg>
                WhatsApp
              </a>
              <div className="flex items-center gap-3 text-white/50 text-sm"><MapPin size={16} /> Kingston, Jamaica</div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs font-body">&copy; {new Date().getFullYear()} Aristocrats JA Signature Events. All rights reserved.</p>
          <p className="text-white/20 text-xs font-body">Where Technology Meets Prestige.</p>
        </div>
      </div>
      <ShareQrModal open={qrOpen} onClose={() => setQrOpen(false)} />
    </footer>
  );
}
